import { Component, OnDestroy, OnInit } from '@angular/core';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { NgFor, NgIf } from '@angular/common';
import { HardwareSocketService } from '../../services/hardware-socket.service';
import { SystemBroadcastService } from '@api/service/system-broadcast.service';
import { SystemBroadcast } from '@api/model/system-broadcast';
import { EcondosQuery } from '@api/model/query';
import { Subject } from 'rxjs';
import { distinctUntilKeyChanged, filter, takeUntil } from 'rxjs/operators';
import { SessionService } from '@api/service/session.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { IconifyComponent } from '../../web-components/iconify/iconify.component';
import swal from 'sweetalert2';
import { User } from '@api/model/user';
import { Condo } from '@api/model/condo';

@Component({
  selector: 'app-broadcast-message',
  templateUrl: 'broadcast-message.component.html',
  styleUrls: ['broadcast-message.component.scss'],
  standalone: true,
  imports: [CarouselModule, NgFor, NgIf, IconifyComponent]
})
export class BroadcastMessageComponent implements OnInit, OnDestroy {
  messages: SystemBroadcast[] = [];
  private socketSubscription;

  destroy$: Subject<void> = new Subject<void>();

  activeIndex = 0;
  user: User;
  condo: Condo;

  constructor(
    private socketService: HardwareSocketService,
    private systemBroadcastService: SystemBroadcastService,
    private sessionService: SessionService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.user = this.sessionService.userValue;
    this.sessionService.selectedCondo$
      .pipe(
        filter(condo => !!condo),
        distinctUntilKeyChanged('_id'),
        takeUntil(this.destroy$)
      )
      .subscribe(condo => {
        this.condo = condo;
        if (this.socketService.connectionStatus$) {
          this.getData();
        }
      });
    this.socketService.onConnect$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.messages = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  getData() {
    if (!this.condo) {
      return;
    }

    const query: EcondosQuery = {
      $select: 'title message htmlMessage createdAt isActive link linkButtonName whiteLabels',
      $populate: [{ path: 'whiteLabels', select: 'webAppUrl' }],
      condos: this.condo._id,
      notificationChannel: { $in: ['WEB', 'WEB_AND_PUSH'] }
    };

    this.systemBroadcastService.getActive(query).subscribe({
      next: ({ systemBroadcasts }) => {
        const messagesArray = this.readFromLocalStorage();
        systemBroadcasts = systemBroadcasts.filter(broadcast => !messagesArray.includes(broadcast._id));
        this.messages = systemBroadcasts.map(systemBroadcast => ({
          ...systemBroadcast,
          safeHtmlMessage: this.sanitizer.bypassSecurityTrustHtml(systemBroadcast.htmlMessage || '')
        }));
        this.listenBroadcasts();
      },
      error: () => {
        this.messages = [];
      }
    });
  }

  listenBroadcasts() {
    //Usuário apenas morador ou apenas zelador não se conectam no socket ../../../services/hardware-socket.service.ts linha 101
    if (this.socketSubscription || !this.socketService.socketConnection) {
      return;
    }
    this.socketSubscription = this.socketService.socketConnection.on('SYSTEM_BROADCAST', res => {
      const systemBroadcast = res.systemBroadcast;
      if (!systemBroadcast) {
        return;
      }
      const message = this.messages.find(b => b._id === systemBroadcast._id);
      if (systemBroadcast.isActive) {
        if (!message) {
          const isInCondos = systemBroadcast.condos.includes(this.condo._id);
          const isInWhiteLabels = systemBroadcast.whiteLabels.includes(this.condo?.whiteLabel);
          const userRoles = this.user.getUserRole(this.condo._id);
          const isInUserType = systemBroadcast.userTypes.some(type => userRoles.includes(type));
          const messagesArray = this.readFromLocalStorage();
          const isSystemAdmin = this.user.isSystemAdmin;
          if (isInCondos && ((!messagesArray.includes(systemBroadcast._id) && isInWhiteLabels && isInUserType) || isSystemAdmin)) {
            this.messages.push({
              ...systemBroadcast,
              safeHtmlMessage: this.sanitizer.bypassSecurityTrustHtml(systemBroadcast.htmlMessage || '')
            });
          }
        }
      } else {
        if (message) {
          this.messages = this.messages.filter(b => b._id !== systemBroadcast._id);
        }
      }
    });
  }

  parseLink(message: SystemBroadcast) {
    this.systemBroadcastService.updateCount(message._id, 'linkButtonClick').subscribe({
      next: () => {
        if (!message.link.startsWith('http')) {
          message.link = 'http://' + message.link;
        }
        if (message.link.includes('.econdos')) {
          const path = new URL(message.link).pathname;
          this.router.navigateByUrl(path);
        } else {
          window.open(message.link, '_blank');
        }
      },
      error: () => {
        this.toastr.error('Falha ao abrir a url');
      }
    });
  }

  askToClose(message: SystemBroadcast) {
    swal({
      type: 'warning',
      title: `Deseja fechar esta mensagem?`,
      text: `Se você fechar, ela não será exibida novamente.
      Você pode escolher ser lembrado mais tarde, se preferir.`,
      showCancelButton: true,
      confirmButtonText: 'Não mostrar novamente',
      confirmButtonColor: '#32DB64',
      cancelButtonColor: '#f53d3d',
      cancelButtonText: 'Lembrar mais tarde',
      reverseButtons: true,
      showLoaderOnConfirm: true,
      width: 550,
      preConfirm: async () => {
        try {
          this.messages = this.messages.filter(broadcast => broadcast._id !== message._id);
          this.saveToLocalStorage(message._id);
        } catch (err) {
          console.log(err);
          this.toastr.error('Não foi realizar a operação, tente novamente');
        }
      }
    }).then(
      () => {
        this.toastr.success('Preferência atualizada com sucesso');
      },
      err => {
        if (err === 'cancel') {
          this.messages = this.messages.filter(broadcast => broadcast._id !== message._id);
        }
      }
    );
  }

  saveToLocalStorage(messageId: string) {
    const localStorageKey = `econdos_system-broadcast_user:${this.user._id}`;
    const storedMessages = localStorage.getItem(localStorageKey);
    let messagesArray = storedMessages ? JSON.parse(storedMessages) : [];

    if (!messagesArray.includes(messageId)) {
      messagesArray.push(messageId);
      localStorage.setItem(localStorageKey, JSON.stringify(messagesArray));
    }
  }

  readFromLocalStorage() {
    const localStorageKey = `econdos_system-broadcast_user:${this.user._id}`;
    const storedMessages = localStorage.getItem(localStorageKey);
    return storedMessages ? JSON.parse(storedMessages) : [];
  }
}
